import React, {useEffect, useMemo, useRef} from 'react';
import {Item} from '@react-stately/collections';
import type {Key} from '@react-types/shared';
import {useTreeState} from '@react-stately/tree';
import {useMenu} from '@react-aria/menu';
import type {AriaMenuProps} from '@react-aria/menu';
import cx from 'classnames';

import {Scrollbars} from '../../elements/Scrollbars/Scrollbars';

import {InnerAriaMenuItem} from './InnerAriaMenuItem';
import type {AriaMenuItem} from './AriaMenu.types';

import styles from './AriaMenu.module.scss';

const buildCollection = (items: AriaMenuItem[]) => {
    // Filter out items whose DOM element would be replaced (e.g. by a
    // createElement override that swaps hidden data-testid elements for
    // Fragments). Keeping them in the react-aria collection while their
    // ref.current is null causes a crash on keyboard navigation.
    const visibleItems = items.filter(item => {
        if (!item.testId) {
            return true;
        }
        return (
            React.createElement('span', {'data-testid': item.testId}).type ===
            'span'
        );
    });

    const itemMap = new Map<Key, AriaMenuItem>();
    for (const item of visibleItems) {
        itemMap.set(item.key, item);
    }

    const disabledKeys = visibleItems
        .filter(item => item.isDisabled)
        .map(item => item.key);

    return {visibleItems, itemMap, disabledKeys};
};

export const InnerAriaMenu: React.FC<{
    items: AriaMenuItem[];
    /**
     * Called with the resolved item when the user selects a menu item. The
     * caller decides whether to open a submenu, run the item action or close
     * the menu.
     */
    onSelect: (item: AriaMenuItem) => void;
    /**
     * Closes the menu when keyboard navigation leaves a menu item.
     */
    onClose: () => void;
    /**
     * Called to navigate back to the previous level when the user presses the
     * "back" arrow key (ArrowLeft in LTR, ArrowRight in RTL). Only provided for
     * submenu levels, so the root menu never navigates back on an arrow key.
     */
    onNavigateBack?: () => void;
    /**
     * Aria props coming from the menu trigger. Only supplied for the root menu
     * so it is correctly associated with the trigger button.
     */
    ariaProps?: Partial<AriaMenuProps<typeof Item>>;
    /**
     * Aria label used when the menu is not associated with a trigger (submenu).
     */
    ariaLabel?: string;
    /**
     * ID of the element that labels a menu not associated with a trigger.
     */
    ariaLabelledBy?: string;
    autoFocus?: AriaMenuProps<typeof Item>['autoFocus'];
    restoreFocusKey?: string;
    onFocusRestored?: () => void;
    testId?: string;
    menuClassName?: string;
    itemClassName?: string;
    showSubMenuIndicator?: boolean;
}> = ({
    items,
    onSelect,
    onClose,
    onNavigateBack,
    ariaProps,
    ariaLabel,
    ariaLabelledBy,
    autoFocus,
    restoreFocusKey,
    onFocusRestored,
    testId,
    menuClassName,
    itemClassName,
    showSubMenuIndicator,
}) => {
    const {visibleItems, itemMap, disabledKeys} = useMemo(
        () => buildCollection(items),
        [items],
    );

    const treeProps: AriaMenuProps<typeof Item> = {
        ...ariaProps,
        'aria-label': ariaProps?.['aria-label'] ?? ariaLabel,
        'aria-labelledby': ariaProps?.['aria-labelledby'] ?? ariaLabelledBy,
        children: visibleItems.map(item => (
            <Item
                textValue={
                    typeof item.label === 'string' ? item.label : item.textValue
                }
                key={item.key}
            >
                {item.label}
            </Item>
        )),
        disabledKeys,
        autoFocus: autoFocus ?? ariaProps?.autoFocus,
        // Set onClose to undefined. This will prevent the menu from closing when
        // the user clicks an item. The decision is made in onSelect for each
        // item individually.
        onClose: undefined,
        onAction: (key: Key) => {
            const item = itemMap.get(key);
            if (item) {
                onSelect(item);
            }
        },
    };

    // Create state based on the incoming props
    const state = useTreeState<typeof Item>(treeProps);

    // Get props for the menu element
    const ref = useRef<HTMLDivElement>(null);
    const {menuProps} = useMenu(treeProps, state, ref);

    useEffect(() => {
        const handleTabFromMenuItem = (event: KeyboardEvent) => {
            const eventTarget = event.target;
            if (
                event.key !== 'Tab' ||
                !(eventTarget instanceof HTMLElement) ||
                eventTarget !== document.activeElement ||
                !ref.current?.contains(eventTarget)
            ) {
                return;
            }
            const eventTargetRole = eventTarget.getAttribute('role');
            if (
                eventTargetRole !== 'menuitem' &&
                eventTargetRole !== 'menuitemcheckbox' &&
                eventTargetRole !== 'menuitemradio'
            ) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            onClose();
        };

        window.addEventListener('keydown', handleTabFromMenuItem, true);
        return () => {
            window.removeEventListener('keydown', handleTabFromMenuItem, true);
        };
    }, [onClose]);

    // Arrow-key navigation between menu levels. The "forward" arrow (ArrowRight
    // in LTR, ArrowLeft in RTL) opens the focused item's submenu, mirroring
    // Enter; the "back" arrow (the opposite) returns to the previous level like
    // the back button. We only act when a focused item actually opens a submenu
    // (forward) or when this menu is itself a submenu (back) — otherwise the key
    // is left to react-aria's default handling. Reading the direction from the
    // computed style at event time keeps it correct for the current layout, and
    // handling in the capture phase lets us pre-empt react-aria's own key
    // handling and stop propagation when (and only when) we act.
    const handleArrowKeyDownCapture = (
        e: React.KeyboardEvent<HTMLDivElement>,
    ) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
            return;
        }
        const isRtl = getComputedStyle(e.currentTarget).direction === 'rtl';
        const forwardKey = isRtl ? 'ArrowLeft' : 'ArrowRight';

        if (e.key === forwardKey) {
            const {focusedKey} = state.selectionManager;
            const focusedItem =
                focusedKey != null ? itemMap.get(focusedKey) : undefined;
            if (focusedItem?.subMenu) {
                e.preventDefault();
                e.stopPropagation();
                onSelect(focusedItem);
            }
            return;
        }

        // The remaining arrow is the "back" key.
        if (onNavigateBack) {
            e.preventDefault();
            e.stopPropagation();
            onNavigateBack();
        }
    };

    return (
        <Scrollbars className={styles.innerMenuScrollbar}>
            <div
                {...menuProps}
                ref={ref}
                onKeyDownCapture={handleArrowKeyDownCapture}
                className={cx(styles.innerMenu, 'px-2', 'py-2', menuClassName)}
                data-testid={testId}
            >
                {[...state.collection].map(item => (
                    <InnerAriaMenuItem
                        itemMap={itemMap}
                        key={item.key}
                        item={item}
                        state={state}
                        itemClassName={itemClassName}
                        showSubMenuIndicator={showSubMenuIndicator}
                        restoreFocus={item.key === restoreFocusKey}
                        onFocusRestored={onFocusRestored}
                    />
                ))}
            </div>
        </Scrollbars>
    );
};
